import { useEffect, useId, useState, type ReactNode } from 'react';
import { ArrowLeft, ChevronRight, Clock, MapPin, Search } from 'lucide-react';
import { useLocale } from '../../../i18n';
import { searchClubsOutcome, type ClubSuggestion } from '../../../lib/api/clubs';
import { bookAtVenue, readVenue, readVenueAvailability } from '../../../lib/api/venues';
import { askToJoinClub, readMyJoinRequests, withdrawJoinRequest } from '../../../lib/api/joinClub';
import { ensureMyPlayer } from '../../../lib/api/myPlayer';
import type { MyClub } from '../../../lib/api/myClubs';
import { BigButton, Card, EmptyState } from '../mobileUi';
import { safeId } from '../../ui/labelledControl';
import { useRetryWait } from '../../ui/useRetryWait';
import { MobileVenue } from './MobileVenue';

// ─────────────────────────────────────────────────────────────────────
// «Finn en klubb» — the public club directory, inside the player app.
//
// It used to be its own full-screen page in front of the app
// (`ClubDiscoveryScreen`, reached from a placeholder home). The app is the
// player's home now (owner, 2026-10-16), so the directory is one of its
// screens, in its own vocabulary, and a membership is something a player may
// ask for from here — never the price of getting in.
//
// ── THE APPLICATION JOURNEY IS UNCHANGED ──────────────────────────
//
// Same reads, same codes, same order: `GET /clubs?q=` to search, `POST
// /clubs/:slug/join-requests` to ask, and when the server answers
// `no_player` the profile is created with the name given and the application
// sent again. Applying uses the identity the session already proved; nothing
// here asks for an address or mints a credential.
//
// ── THE DIRECTORY'S OUTAGE IS NOT «NO MATCHES» (#832 review) ──────
//
// `searchClubsOutcome`, not `searchClubs`: the latter answers `[]` for a
// failure, which would tell somebody their club does not exist.
//
// ── A CLUB YOU ARE ALREADY IN IS SELECTED, NOT APPLIED TO ─────────
//
// A search result the player is on the roster of offers «Velg» — the context
// selection — rather than an application the server would refuse.
//
// ── A RESULT LEADS SOMEWHERE (#833) ───────────────────────────────
//
// Tapping a result's name opens the venue's own page (`MobileVenue`): its
// courts, its opening hours, and the way on. The button beside the name is
// unchanged, so the application journey above is still one tap from the list.
// The page's own «Søk om medlemskap» runs through the SAME `apply` below — one
// set of outcomes and one profile step, whichever screen it was tapped on.
//
// ── AN APPLICATION THAT STANDS SAYS SO ON THE BUTTON ──────────────
//
// Once a club has the application, its button reads «Venter på godkjenning»
// and cannot be tapped — on the list and on the venue page alike. The clubs
// already asked come from `GET /me/join-requests` when the screen opens, so
// the answer survives a reload rather than living only in this component's
// memory; an application that stands after a tap is added at once. A failed
// read costs only the ordinary button, which is safe to tap: the server
// answers a standing application with that application.
//
// ── A REFUSAL THAT STANDS SAYS SO TOO, WITH ITS DATE (#1302) ──────
//
// A club that rejected somebody with a cooling-off recorded a date, and the
// server would refuse every ask before it. The button used to stay enabled
// and answer «Du kan søke her igjen senere» — an invitation to something that
// will be refused, and no word on when it will work. The same read now says
// which clubs are closed to this person (`closed`, decided by the server's
// own gate), and the button is DISABLED with «Du kan søke igjen fra {dato}»
// beside it until that date (owner decision 2026-11-02, option A). «Never
// again» says so. The date is formatted for DISPLAY only, through the locale.
// ─────────────────────────────────────────────────────────────────────

export interface MobileFindClubProps {
  /** The player's own clubs, so a result they belong to is selected instead. */
  memberships: MyClub[];
  onSelect: (club: MyClub) => void;
  /** Select a club the player is in and open its booking (#833). */
  onBook?: (club: MyClub) => void;
  /**
   * Where an account with no player profile makes one — Profil's «Opprett
   * spillerprofil» (#833 slice 3b). A venue page that cannot book for somebody
   * without a profile points there.
   */
  onCreateProfile?: () => void;
  onBack: () => void;
  /** Injected in tests; the real ones are the directory and the join door. */
  search?: typeof searchClubsOutcome;
  ask?: typeof askToJoinClub;
  provisionPlayer?: typeof ensureMyPlayer;
  loadVenue?: typeof readVenue;
  loadAvailability?: typeof readVenueAvailability;
  loadPending?: typeof readMyJoinRequests;
  withdraw?: typeof withdrawJoinRequest;
  /** Injected in tests; the real one is the venue's own door (#833 slice 3b). */
  bookVenue?: typeof bookAtVenue;
}

/** What happened to the one club this person last asked about. */
type Asked = { slug: string; code: string | null; created: boolean; from: string | null };
/** Why a club will refuse this person right now (#1302). */
type Closed = { code: string; from: string | null };

/** The refusal codes that mean «not now» rather than «that went wrong». */
const CLOSING_CODES: ReadonlySet<string> = new Set([
  'reapply_not_yet',
  'reapply_blocked',
  'request_closed',
]);

/**
 * Does this refusal still stand? A cooling-off whose date has passed does not:
 * the server's gate opens at that instant, and so does the button.
 */
export function closedStillStands(closed: Closed | undefined, now: number): boolean {
  if (!closed) return false;
  if (closed.code !== 'reapply_not_yet') return true;
  const from = closed.from === null ? NaN : Date.parse(closed.from);
  // No readable date: the ask said «not yet» and will say it again — but the
  // button is not held shut on a date nobody can show.
  return Number.isFinite(from) && from > now;
}
type Results = { state: 'idle' } | { state: 'ok'; clubs: ClubSuggestion[] } | { state: 'failed' };

export function MobileFindClub({
  memberships,
  onSelect,
  onBook,
  onCreateProfile,
  onBack,
  search = searchClubsOutcome,
  ask = askToJoinClub,
  provisionPlayer = ensureMyPlayer,
  loadVenue = readVenue,
  loadAvailability = readVenueAvailability,
  loadPending = readMyJoinRequests,
  withdraw = withdrawJoinRequest,
  bookVenue,
}: MobileFindClubProps) {
  const { t, formatDate } = useLocale();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Results>({ state: 'idle' });
  const [busy, setBusy] = useState<string | null>(null);
  /** The club whose application is being taken back (#729). */
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [asked, setAsked] = useState<Asked | null>(null);
  /** The clubs this person has an application standing with. */
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  /** The clubs that will refuse this person right now, and why (#1302). */
  const [closed, setClosed] = useState<ReadonlyMap<string, Closed>>(() => new Map());
  /** The result whose venue page is open, or null for the list. */
  const [open, setOpen] = useState<ClubSuggestion | null>(null);
  /** The club whose application is waiting for a profile, and the name typed. */
  const [needsProfile, setNeedsProfile] = useState<string | null>(null);
  // THE JOIN BUDGET IS PER ACCOUNT (#710), so a spent one holds every «Søk»
  // button, not only the club that answered — and for exactly as long as the
  // server said (#778 Q7).
  const wait = useRetryWait<'ask'>();
  const asking = busy !== null || wait.waiting('ask');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  // Only an empty name is the field's fault; a failed create is described by
  // the same message but does not make what was typed invalid.
  const [nameInvalid, setNameInvalid] = useState(false);
  const auto = useId();
  const nameErrorId = safeId('find-club-name-error', auto);

  useEffect(() => {
    const controller = new AbortController();
    void loadPending(controller.signal).then((standing) => {
      if (controller.signal.aborted) return;
      if (standing.pending.length > 0)
        setPending((prev) => new Set([...prev, ...standing.pending]));
      if (standing.closed.length > 0)
        setClosed((prev) => {
          const next = new Map(prev);
          for (const c of standing.closed) next.set(c.slug, { code: c.code, from: c.from });
          return next;
        });
    });
    return () => controller.abort();
  }, [loadPending]);

  /** Record the outcome, and — when the application now stands — that it does. */
  const settle = (
    slug: string,
    outcome: {
      code: string | null;
      created: boolean;
      from?: string | null;
      retryAfterSeconds?: number;
    },
  ) => {
    if (outcome.code === 'too_many_attempts') wait.start('ask', outcome.retryAfterSeconds ?? 0);
    const from = outcome.from ?? null;
    setAsked({ slug, code: outcome.code, created: outcome.created, from });
    if (outcome.code === null) setPending((prev) => new Set(prev).add(slug));
    // A refusal learned by tapping holds the button exactly as one read at
    // load would: the person has just been told why, and when.
    const code = outcome.code;
    if (code !== null && CLOSING_CODES.has(code))
      setClosed((prev) => new Map(prev).set(slug, { code, from }));
  };

  /**
   * The day they may ask again, for DISPLAY only (CLAUDE.md on dates): «2.
   * des.» this year, with the year when it is another one. Never parsed back.
   */
  const reapplyDate = (iso: string): string | null => {
    const at = new Date(iso);
    if (!Number.isFinite(at.getTime())) return null;
    const sameYear = at.getFullYear() === new Date().getFullYear();
    return formatDate(at, {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  };

  /** The live refusal for this club, or null when asking is open. */
  const closedFor = (slug: string): Closed | null => {
    const entry = closed.get(slug);
    return closedStillStands(entry, Date.now()) ? (entry ?? null) : null;
  };

  useEffect(() => {
    const term = query.trim();
    if (term === '') {
      setResults({ state: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    const timer = setTimeout(() => {
      void search(term, controller.signal).then((outcome) => {
        // A superseded keystroke is how a type-ahead ends, not a failure.
        if (cancelled || outcome.status === 'aborted') return;
        setResults(
          outcome.status === 'ok' ? { state: 'ok', clubs: outcome.clubs } : { state: 'failed' },
        );
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, search]);

  const messageFor = (code: string | null, created: boolean, from: string | null): ReactNode => {
    if (code === null) return created ? t('clubDiscovery.asked') : t('clubDiscovery.alreadyAsked');
    switch (code) {
      // The outcomes of taking an application back (#729), carried in the
      // same slot so the sentence sits with the club it is about.
      case 'withdrawn':
        return t('clubDiscovery.withdrawn');
      case 'not_pending':
        return t('clubDiscovery.withdrawAnswered');
      case 'already_member':
        return t('clubDiscovery.alreadyMember');
      case 'no_player':
        return t('clubDiscovery.noPlayer');
      case 'too_many_attempts':
        // THE RATE BUDGET, not the standing cap: it clears with time, so the
        // sentence is the wait, and it goes away when the wait does.
        return wait.notice;
      case 'too_many_pending':
        return t('clubDiscovery.tooMany');
      case 'request_closed':
        return t('clubDiscovery.closed');
      // «NEVER AGAIN» SAYS SO (#1302): the club chose it, and «not right now»
      // would promise a door that will not open.
      case 'reapply_blocked':
        return t('clubDiscovery.blocked');
      case 'reapply_not_yet': {
        const date = from === null ? null : reapplyDate(from);
        return date === null ? t('clubDiscovery.notYet') : t('clubDiscovery.notYetFrom', { date });
      }
      default:
        return t('clubDiscovery.failed');
    }
  };

  const apply = async (slug: string) => {
    if (asking) return;
    wait.release('ask');
    setBusy(slug);
    const outcome = await ask(slug);
    setBusy(null);
    if (outcome.code === 'no_player') {
      setNeedsProfile(slug);
      setNameError(null);
      return;
    }
    settle(slug, outcome);
  };

  /**
   * Take a standing application back (#729). Withdrawn — or already gone —
   * means the button returns, because the person may apply again at once.
   * `not_pending` means the club answered first: the application no longer
   * stands either, and they are told so.
   */
  const takeBack = async (slug: string) => {
    if (withdrawing) return;
    setWithdrawing(slug);
    const outcome = await withdraw(slug);
    setWithdrawing(null);
    const gone = outcome.code === null || outcome.code === 'no_request';
    if (gone || outcome.code === 'not_pending') {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(slug);
        return next;
      });
    }
    setAsked({ slug, code: gone ? 'withdrawn' : outcome.code, created: false, from: null });
  };

  /** Create the profile with the name given, then finish the application. */
  const createAndApply = async () => {
    const slug = needsProfile;
    if (!slug || busy) return;
    const given = name.trim();
    if (given === '') {
      setNameError(t('clubDiscovery.nameRequired'));
      setNameInvalid(true);
      return;
    }
    setNameInvalid(false);
    setBusy(slug);
    const provisioned = await provisionPlayer(given);
    if (provisioned.code !== null) {
      setBusy(null);
      setNameError(t('clubDiscovery.failed'));
      return;
    }
    const outcome = await ask(slug);
    setBusy(null);
    setNeedsProfile(null);
    setName('');
    settle(slug, outcome);
  };

  const memberOf = (slug: string) => memberships.find((m) => m.slug === slug) ?? null;

  const kindLabel = (kind: string) =>
    kind === 'CENTRE'
      ? t('clubDiscovery.kindCentre')
      : kind === 'CLUB'
        ? t('clubDiscovery.kindClub')
        : null;

  // THE NAME STEP, wherever the application was started from — the list or a
  // venue page. One errand: the club has been chosen and this is the last
  // thing it needs.
  const nameStep =
    needsProfile === null ? null : (
      <Card>
        <h2 className="font-display text-[16px] font-semibold text-ink">
          {t('clubDiscovery.needsProfileTitle')}
        </h2>
        <p className="text-[12px] text-ink-3 mt-1 mb-3">{t('clubDiscovery.needsProfileLead')}</p>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
            {t('clubDiscovery.nameLabel')}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="w-full rounded-xl px-3 py-2 text-[14px] text-ink"
            style={{ background: '#eef0ea' }}
            disabled={busy !== null}
            // Announced when it appears, and read again with the field (#900).
            aria-invalid={nameInvalid || undefined}
            aria-describedby={nameError ? nameErrorId : undefined}
          />
        </label>
        {nameError && (
          <p id={nameErrorId} role="alert" className="text-[12px] text-danger mt-2">
            {nameError}
          </p>
        )}
        <div className="mt-3 space-y-2">
          <BigButton onClick={() => void createAndApply()} disabled={busy !== null}>
            {busy !== null ? t('clubDiscovery.creating') : t('clubDiscovery.createAndApply')}
          </BigButton>
          <BigButton
            variant="ghost"
            onClick={() => {
              setNeedsProfile(null);
              setNameError(null);
            }}
            disabled={busy !== null}
          >
            {t('clubDiscovery.cancel')}
          </BigButton>
        </div>
      </Card>
    );

  /**
   * What to say under a club's button: the answer to the last ask about it,
   * else the refusal that stands (#1302) — said before anybody taps.
   */
  const noteFor = (slug: string): ReactNode => {
    if (asked?.slug === slug) return messageFor(asked.code, asked.created, asked.from);
    const shut = closedFor(slug);
    return shut ? messageFor(shut.code, false, shut.from) : null;
  };

  if (open) {
    const mine = memberOf(open.slug);
    return (
      <MobileVenue
        key={open.slug}
        slug={open.slug}
        fallbackName={open.name}
        kind={open.kind}
        membership={mine}
        onBack={() => setOpen(null)}
        onSelect={onSelect}
        onBook={onBook}
        onApply={() => void apply(open.slug)}
        applying={busy === open.slug}
        applyDisabled={asking}
        closed={closedFor(open.slug) !== null}
        pending={pending.has(open.slug)}
        onWithdraw={() => void takeBack(open.slug)}
        withdrawing={withdrawing === open.slug}
        outcome={noteFor(open.slug)}
        profileStep={needsProfile === open.slug ? nameStep : undefined}
        load={loadVenue}
        loadAvailability={loadAvailability}
        {...(onCreateProfile ? { onCreateProfile } : {})}
        {...(bookVenue ? { bookAtVenue: bookVenue } : {})}
      />
    );
  }

  return (
    <div className="px-4 pb-4 space-y-3">
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={onBack}
          aria-label={t('mobile.common.back')}
          className="p-1 -ml-1 text-ink-3"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display font-semibold text-ink leading-tight" style={{ fontSize: 26 }}>
          {t('clubDiscovery.title')}
        </h1>
      </div>
      <p className="text-[12px] text-ink-3 px-1">{t('clubDiscovery.lead')}</p>

      <label
        className="flex items-center gap-2 rounded-xl px-3 py-2"
        style={{ background: '#eef0ea' }}
      >
        <Search size={15} className="text-ink-3" aria-hidden="true" />
        <span className="sr-only">{t('clubDiscovery.searchLabel')}</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('clubDiscovery.placeholder')}
          className="flex-1 bg-transparent text-[14px] text-ink"
        />
      </label>

      {nameStep}

      {needsProfile === null && results.state === 'failed' && (
        <EmptyState title={t('clubDiscovery.unavailable')} />
      )}
      {needsProfile === null && results.state === 'ok' && results.clubs.length === 0 && (
        <EmptyState title={t('clubDiscovery.empty')} />
      )}
      {needsProfile === null && results.state === 'ok' && results.clubs.length > 0 && (
        <div className="space-y-2">
          {results.clubs.map((club) => {
            const mine = memberOf(club.slug);
            const shut = closedFor(club.slug) !== null;
            const note = noteFor(club.slug);
            const noteId = `${safeId('find-club-note', auto)}-${club.slug}`;
            return (
              <Card key={club.slug}>
                <div className="flex items-center gap-3">
                  {/* THE RESULT ITSELF OPENS THE VENUE (#833). Its own button,
                      beside — never around — the action on the right. */}
                  <button
                    type="button"
                    className="flex items-center gap-3 min-w-0 flex-1 text-left"
                    aria-label={t('clubDiscovery.open', { name: club.name })}
                    onClick={() => setOpen(club)}
                  >
                    <div
                      className="flex items-center justify-center rounded-2xl flex-shrink-0"
                      style={{ width: 42, height: 42, background: '#eef0ea', color: '#0f1611' }}
                    >
                      <MapPin size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      {/* TWO LINES, NOT AN ELLIPSIS: the name is what is being
                          chosen, and «Smaalenene P…» does not say which. */}
                      <div className="text-[14px] font-semibold text-ink leading-snug line-clamp-2">
                        {club.name}
                      </div>
                      {/* The chevron sits in the subtitle so it costs the name
                          no width — the action pill already takes most of it. */}
                      <div className="flex items-center gap-0.5 text-[11px] text-ink-3">
                        {kindLabel(club.kind)}
                        <ChevronRight size={12} aria-hidden="true" />
                      </div>
                    </div>
                  </button>
                  {mine ? (
                    <button
                      type="button"
                      className="rounded-full px-3 py-1.5 text-[12px] font-semibold flex-shrink-0"
                      style={{ background: '#c2f02f', color: '#0f1611' }}
                      onClick={() => onSelect(mine)}
                    >
                      {t('clubDiscovery.select')}
                    </button>
                  ) : pending.has(club.slug) ? (
                    // NOT A BUTTON THAT DOES NOTHING: a status, so nobody taps
                    // it expecting a second application to be sent.
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-semibold flex-shrink-0 text-ink-3"
                      style={{ background: '#eef0ea' }}
                    >
                      <Clock size={12} aria-hidden="true" />
                      {t('clubDiscovery.pending')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="rounded-full px-3 py-1.5 text-[12px] font-semibold flex-shrink-0 disabled:opacity-40"
                      style={{ background: '#0f1611', color: '#fff' }}
                      onClick={() => void apply(club.slug)}
                      // HELD SHUT WHILE THE CLUB'S ANSWER STANDS (#1302), and
                      // the reason is read with the button.
                      disabled={asking || shut}
                      aria-describedby={shut && note ? noteId : undefined}
                    >
                      {busy === club.slug ? t('clubDiscovery.asking') : t('clubDiscovery.ask')}
                    </button>
                  )}
                </div>
                {/* THE OUTCOME SITS WITH THE CLUB IT IS ABOUT. One message
                    floating under a list does not say WHICH club answered. */}
                {note && (
                  <p id={noteId} className="text-[12px] text-ink-3 mt-2">
                    {note}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
